import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Divider,
  Avatar,
  Rating,
  Drawer,
  Switch,
  InputAdornment,
  Snackbar,
  Alert as MuiAlert,
  useTheme,
  useMediaQuery,
  CardActionArea,
  CardHeader,
  TablePagination,
  Fab,
  Zoom
} from '@mui/material';
import {
  Close,
  CheckCircle,
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
  CheckCircleOutline,
  ExpandMore,
  Download,
  Add,
  Remove,
  PriorityHigh,
  Analytics,
  TrendingUp,
  Savings,
  RocketLaunch,
  CrisisAlert,
  WorkspacePremium,
  AutoGraph,
  Psychology,
  Public,
  Security,
  Groups,
  School,
  AccountTree,
  Balance,
  Calculate,
  Timeline,
  Insights,
  BarChart,
  PieChart,
  TableChart,
  ViewQuilt,
  Search,
  FilterList,
  Sort,
  Dashboard,
  Assessment,
  Share,
  Print,
  ContentCopy,
  Lightbulb,
  Flag,
  CorporateFare,
  EmojiObjects,
  Speed,
  Engineering,
  CloudQueue,
  DataObject,
  Schema,
  IntegrationInstructions,
  PsychologyAlt,
  Biotech,
  PrecisionManufacturing,
  LocalShipping,
  Inventory2,
  MonetizationOn,
  AccountBalance,
  ShowChart,
  GroupWork,
  Api,
  Storage,
  AdminPanelSettings,
  BugReport,
  Build,
  Architecture,
  Eco,
  AutoAwesome,
  TrendingFlat,
  ArrowForward,
  ArrowBack,
  ZoomIn,
  ZoomOut,
  Settings,
  Help,
  TaskAlt,
  Cancel,
  Schedule,
  CalendarToday,
  TrackChanges,
  AltRoute,
  ScatterPlot,
  BubbleChart,
  DonutSmall,
  MultilineChart,
  StackedLineChart,
  WaterfallChart,
  TableView,
  ViewList,
  FormatListBulleted,
  Ballot,
  FactCheck,
  PlaylistAddCheck,
  DoneAll,
  Lens,
  Favorite,
  ThumbUp,
  ThumbDown,
  SentimentVerySatisfied,
  SentimentVeryDissatisfied,
  Visibility,
  VisibilityOff,
  Lock,
  VpnKey,
  VerifiedUser,
  GppGood,
  GppBad,
  NewReleases,
  Notifications,
  NotificationsActive,
  LiveHelp,
  QuestionMark,
  Feedback,
  RateReview,
  Mail,
  Phone,
  Chat,
  Forum,
  StarRate,
  AttachMoney,
  Payments,
  PriceCheck,
  AccountBalanceWallet,
  CreditCard,
  ReceiptLong,
  Article,
  EditNote,
  FormatQuote,
  Palette,
  AutoFixHigh,
  Handyman,
  DesignServices,
  Functions,
  Dataset,
  CloudDownload,
  CloudUpload,
  Cached,
  Refresh,
  Download as DownloadIcon,
  Upload,
  Folder,
  Science,
  DeveloperBoard,
  Devices,
  Computer,
  Smartphone,
  Event,
  ConfirmationNumber,
  MilitaryTech,
  QueryStats,
  Timeline as TimelineIcon2,
  AnalyticsOutlined,
  Send,
  Share as ShareIcon,
  Reply,
  MarkEmailRead,
  Email,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Report,
  Campaign,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Help as HelpIcon,
  ContactSupport,
  Reviews,
  CurrencyExchange,
  Euro,
  AutoFixNormal,
  TrendingDown,
  Assessment as AssessmentIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  ViewQuilt as ViewQuiltIcon,
  PictureAsPdf,
  Description
} from '@mui/icons-material';

// Import the comprehensive framework data
import { GCC_TRANSFORMATION_FRAMEWORK, COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK } from '../../data/gccFramework';
import { GCC_VALUE_LEAK_INDICATORS, getIndicatorsBySubType } from '../../data/gccValueLeakIndicators';

// Enhanced framework with complete integration
const ENHANCED_FRAMEWORK = {
  ...COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK,
  gccTransformation: GCC_TRANSFORMATION_FRAMEWORK,
  
  // Enhanced value recovery roadmap generator
  generateComprehensiveRecoveryRoadmap: (painPoints, companyContext) => {
    const roadmap = {
      executiveSummary: {
        totalPainPoints: painPoints.length,
        immediatePriority: painPoints.filter(p => p.priority === 'IMMEDIATE').length,
        highPriority: painPoints.filter(p => p.priority === 'HIGH').length,
        totalAddressableValue: `$${Math.round(painPoints.length * 25)}M`,
        implementationTimeline: '18-36 months',
        gccImpact: '40-60% efficiency improvement'
      },
      
      phase1: {
        name: "Foundation & Quick Wins (0-6 months)",
        focus: "Immediate value recovery and capability building",
        initiatives: [],
        expectedValue: `$${Math.round(painPoints.filter(p => p.priority === 'IMMEDIATE').length * 8)}M`,
        keyMetrics: ['Cost reduction %', 'Process efficiency gain', 'Revenue recovery']
      },
      
      phase2: {
        name: "Acceleration & Transformation (6-18 months)",
        focus: "Strategic capability building and digital transformation",
        initiatives: [],
        expectedValue: `$${Math.round(painPoints.filter(p => p.priority === 'HIGH').length * 15)}M`,
        keyMetrics: ['Digital capability maturity', 'Innovation velocity', 'Talent productivity']
      },
      
      phase3: {
        name: "Strategic Leadership (18-36 months)",
        focus: "Market leadership and future readiness",
        initiatives: [],
        expectedValue: `$${Math.round(painPoints.filter(p => p.priority === 'MEDIUM').length * 10)}M`,
        keyMetrics: ['Market leadership position', 'Future capability readiness', 'Strategic optionality value']
      },
      
      gccIntegration: {
        stage1: "Strategic Conception & Validation (Months 1-4)",
        stage2: "Foundation Building & Operational Launch (Months 5-18)",
        stage3: "Scaling & Value Demonstration (Years 2-4)",
        stage4: "Strategic Partnership & Innovation (Years 4-6)",
        stage5: "Business Transformation & Market Leadership (Years 6-8)"
      },
      
      detailedInitiatives: []
    };

    // Generate detailed initiatives for each pain point
    painPoints.forEach((point, index) => {
      const initiative = {
        id: `initiative-${index + 1}`,
        painPoint: point.name,
        category: point.category,
        priority: point.priority,
        businessCase: point.businessCase,
        solutionApproach: generateSolutionApproach(point),
        implementationSteps: generateImplementationSteps(point),
        timeline: getTimelineForPriority(point.priority),
        resourcesRequired: generateResourceRequirements(point),
        successMetrics: generateSuccessMetrics(point),
        risks: generateRisks(point),
        gccIntegration: generateGCCIntegration(point),
        expectedValue: point.recoveryPotential,
        kpis: generateKPIs(point)
      };
      
      roadmap.detailedInitiatives.push(initiative);
      
      // Add to appropriate phase
      if (point.priority === 'IMMEDIATE') {
        roadmap.phase1.initiatives.push(initiative);
      } else if (point.priority === 'HIGH') {
        roadmap.phase2.initiatives.push(initiative);
      } else {
        roadmap.phase3.initiatives.push(initiative);
      }
    });

    return roadmap;
  }
};

// Helper functions for roadmap generation
const generateSolutionApproach = (painPoint) => {
  const approaches = {
    financial: "Implement automated monitoring and optimization systems with centralized governance",
    strategic: "Establish intelligence functions and market sensing capabilities with agile decision-making",
    digital: "Deploy modern technology stack with data-driven optimization and AI capabilities",
    operational: "Streamline processes with automation and continuous improvement frameworks",
    humanCapital: "Develop talent ecosystems with retention strategies and capability building",
    customer: "Implement customer-centric processes with personalized engagement models",
    compliance: "Establish proactive compliance frameworks with automated monitoring",
    innovation: "Create innovation pipelines with rapid experimentation and commercialization",
    technology: "Modernize infrastructure with cloud-native architectures and API-first approaches",
    process: "Optimize workflows with digital automation and performance management",
    data: "Implement data governance with quality frameworks and monetization strategies",
    ecosystem: "Build partnership networks with value-sharing models and platform strategies"
  };
  
  return approaches[painPoint.categoryId] || "Implement comprehensive optimization framework with measurable outcomes";
};

const generateImplementationSteps = (painPoint) => [
  "Conduct detailed assessment and baseline measurement",
  "Develop solution architecture and implementation plan",
  "Secure executive sponsorship and resource allocation",
  "Execute phased implementation with agile methodology",
  "Establish monitoring framework and performance tracking",
  "Conduct continuous improvement and optimization cycles"
];

const getTimelineForPriority = (priority) => {
  const timelines = {
    'IMMEDIATE': '2-4 weeks assessment + 8-12 weeks implementation',
    'HIGH': '4-6 weeks assessment + 12-20 weeks implementation', 
    'MEDIUM': '6-8 weeks assessment + 20-30 weeks implementation',
    'LOW': '8-12 weeks assessment + 30-52 weeks implementation'
  };
  return timelines[priority] || '8-12 weeks assessment + 20-30 weeks implementation';
};

const generateResourceRequirements = (painPoint) => ({
  team: ['Project Manager', 'Domain Expert', 'Technical Specialist', 'Business Analyst'],
  tools: ['Analytics Platform', 'Automation Tools', 'Monitoring Systems', 'Reporting Dashboard'],
  budget: `$${Math.round(painPoint.painScore * 50000)} - $${Math.round(painPoint.painScore * 200000)}`,
  timeline: getTimelineForPriority(painPoint.priority)
});

const generateSuccessMetrics = (painPoint) => [
  `Reduce ${painPoint.name.toLowerCase()} by ${painPoint.recoveryPotential}`,
  `Achieve ${painPoint.painScore >= 8 ? '90%' : painPoint.painScore >= 6 ? '80%' : '70%'} target compliance`,
  `Implement automated monitoring with real-time alerts`,
  `Establish continuous improvement feedback loops`
];

const generateRisks = (painPoint) => [
  "Resistance to change from existing processes",
  "Technical integration challenges with legacy systems",
  "Resource constraints impacting implementation velocity",
  "Regulatory compliance requirements evolution"
];

const generateGCCIntegration = (painPoint) => ({
  capability: `GCC ${painPoint.category} Excellence Center`,
  team: 'Specialized pod with domain expertise',
  tools: 'Standardized platforms and automation frameworks',
  metrics: 'Value delivery tracking and performance dashboards'
});

const generateKPIs = (painPoint) => [
  `${painPoint.recoveryPotential} value realization`,
  '90%+ process compliance',
  'Real-time monitoring and alerting',
  'Quarterly value assurance reviews'
];

// Utility functions
const safeObjectEntries = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return [];
  }
  return Object.entries(obj);
};

const getPriorityConfig = (priority) => {
  const configs = {
    IMMEDIATE: { icon: <ErrorOutline color="error" />, color: 'error', label: 'Immediate' },
    HIGH: { icon: <WarningAmber color="warning" />, color: 'warning', label: 'High' },
    MEDIUM: { icon: <InfoOutlined color="info" />, color: 'info', label: 'Medium' },
    LOW: { icon: <CheckCircleOutline color="success" />, color: 'success', label: 'Low' },
    LONG_TERM: { icon: <CheckCircleOutline color="success" />, color: 'success', label: 'Long Term' }
  };
  return configs[priority] || configs.MEDIUM;
};

// Enhanced Framework Overview Component
const EnhancedFrameworkOverview = ({ frameworkData, categories, businessCaseMetrics, companyContext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
        🎯 Comprehensive Value Leakage Framework
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom color="text.primary">
            Intelligent Value Leakage Detection & Recovery
          </Typography>
          <Typography variant="body1" paragraph color="text.secondary">
            Identify, quantify, and recover value across {categories.length} strategic leakage categories with 
            AI-powered detection models and proven recovery roadmaps. Our framework integrates {Object.keys(GCC_VALUE_LEAK_INDICATORS).reduce((acc, cat) => acc + Object.keys(GCC_VALUE_LEAK_INDICATORS[cat]).length, 0)}+ 
            expert-curated indicators for precise leakage identification.
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'primary.50', p: 2, textAlign: 'center' }}>
            <Typography variant="h3" color="primary.main" fontWeight="bold">
              {categories.length}
            </Typography>
            <Typography variant="body2" color="primary.main">
              Leakage Categories
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50' }}>
            <Typography variant="h5" color="success.main" fontWeight="bold">
              $50-650M
            </Typography>
            <Typography variant="body2">Recoverable Value Range</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50' }}>
            <Typography variant="h5" color="info.main" fontWeight="bold">
              25-40%
            </Typography>
            <Typography variant="body2">GCC Efficiency Impact</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50' }}>
            <Typography variant="h5" color="warning.main" fontWeight="bold">
              98
            </Typography>
            <Typography variant="body2">Leakage Sub-Types</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.50' }}>
            <Typography variant="h5" color="secondary.main" fontWeight="bold">
              70+
            </Typography>
            <Typography variant="body2">Quantification Models</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* GCC Transformation Framework */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: 'primary.50' }} elevation={2}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          🏢 GCC Transformation Framework Integration
        </Typography>
        <Typography variant="body2" paragraph>
          This analysis integrates with the 7-stage GCC Transformation Framework to ensure seamless value recovery and capability building.
        </Typography>
        <Grid container spacing={2}>
          {Object.values(frameworkData.gccTransformation?.stages || {}).slice(0, 4).map(stage => (
            <Grid item xs={12} sm={6} md={3} key={stage.id}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h6" color="primary.main" fontWeight="bold">
                    Stage {stage.id}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {stage.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stage.duration}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Categories Grid */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        📊 Value Leakage Categories
      </Typography>
      <Grid container spacing={2}>
        {categories.map(([categoryKey, category]) => (
          <Grid item xs={12} sm={6} md={4} key={categoryKey}>
            <Card sx={{ 
              height: '100%',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ bgcolor: category.color || 'primary.main', width: 40, height: 40 }}>
                    {category.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {category.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Weight: {category.weight}% • Impact: {category.impact}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {category.description}
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip 
                    label={`${Object.keys(category.subTypes || {}).length} sub-types`} 
                    size="small" 
                    variant="outlined" 
                  />
                  <Box display="flex" gap={1}>
                    <Chip 
                      label={category.impact} 
                      size="small"
                      color={
                        category.impact === 'VERY HIGH' ? 'error' :
                        category.impact === 'HIGH' ? 'warning' : 'default'
                      }
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Enhanced Leakage Detection Component
const EnhancedLeakageDetection = ({ 
  categories,
  filteredSubTypes,
  selectedLeakages,
  onLeakageToggle,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  selectedPriority,
  setSelectedPriority,
  autoSelectMode,
  setAutoSelectMode,
  showIndicators,
  setShowIndicators,
  selectedIndicators,
  onIndicatorToggle,
  onSelectAll,
  onSelectNone
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Handle category selection (select all sub-types in category)
  const handleCategorySelect = useCallback((categoryKey, select) => {
    const category = categories.find(([key]) => key === categoryKey)?.[1];
    if (!category?.subTypes) return;

    const newSelection = { ...selectedLeakages };
    Object.keys(category.subTypes).forEach(subTypeKey => {
      const leakageKey = `${categoryKey}.${subTypeKey}`;
      newSelection[leakageKey] = select;
    });
    
    onLeakageToggle(null, null, null, newSelection);
  }, [categories, selectedLeakages, onLeakageToggle]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
            🔍 Detect Value Leakages
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select identified leakage points across {categories.length} categories using expert-curated indicators
          </Typography>
        </Box>
        <Chip 
          label={`${Object.values(selectedLeakages).filter(Boolean).length} selected`}
          color="primary"
          variant="filled"
          size="large"
        />
      </Box>

      {/* Control Panel */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search leakages, indicators, symptoms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(([key, category]) => (
                  <MenuItem key={key} value={key}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={selectedPriority}
                label="Priority"
                onChange={(e) => setSelectedPriority(e.target.value)}
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="IMMEDIATE">Immediate</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoSelectMode}
                  onChange={(e) => setAutoSelectMode(e.target.checked)}
                  color="primary"
                />
              }
              label="Auto-Select"
            />
          </Grid>
        </Grid>

        {/* Bulk Actions */}
        <Box display="flex" gap={2} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CheckCircle />}
            onClick={onSelectAll}
            size="small"
          >
            Select All
          </Button>
          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={onSelectNone}
            size="small"
          >
            Select None
          </Button>
        </Box>
      </Paper>

      {/* Categories with Sub-Types */}
      {categories.map(([categoryKey, category]) => {
        const categorySubTypes = filteredSubTypes.filter(item => item.categoryKey === categoryKey);
        if (categorySubTypes.length === 0) return null;

        const selectedInCategory = categorySubTypes.filter(item => selectedLeakages[item.leakageKey]).length;
        const allSelected = selectedInCategory === categorySubTypes.length;
        const someSelected = selectedInCategory > 0 && selectedInCategory < categorySubTypes.length;

        return (
          <Accordion key={categoryKey} defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleCategorySelect(categoryKey, !allSelected);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <Avatar sx={{ bgcolor: category.color || 'primary.main', width: 32, height: 32 }}>
                  {category.icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">
                    {category.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.description} • {categorySubTypes.length} sub-types
                  </Typography>
                </Box>
                <Chip 
                  label={`${selectedInCategory}/${categorySubTypes.length} selected`}
                  size="small"
                  variant="outlined"
                  color={allSelected ? "success" : someSelected ? "warning" : "default"}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {categorySubTypes.map(({ leakageKey, subType, priority }) => {
                  const isSelected = selectedLeakages[leakageKey];
                  const priorityConfig = getPriorityConfig(priority);
                  const indicators = subType.indicators || [];
                  const selectedIndicatorCount = selectedIndicators[leakageKey]?.length || 0;

                  return (
                    <Grid item xs={12} key={leakageKey}>
                      <Card 
                        sx={{ 
                          border: isSelected ? 2 : 1,
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: theme.shadows[4]
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box display="flex" alignItems="flex-start" gap={2}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => onLeakageToggle(leakageKey)}
                              color="primary"
                            />
                            
                            <Box sx={{ flex: 1 }}>
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                <Box>
                                  <Typography variant="h6" fontWeight="medium">
                                    {subType.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Impact: {subType.impact} • Recovery: {subType.recovery}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Tooltip title={priorityConfig.label}>
                                    {priorityConfig.icon}
                                  </Tooltip>
                                  <Chip 
                                    label={priority} 
                                    size="small"
                                    color={priorityConfig.color}
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>

                              <Typography variant="body2" paragraph sx={{ mb: 2 }}>
                                {subType.businessCase}
                              </Typography>

                              {/* Indicators Section */}
                              {indicators.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      🎯 Detection Indicators ({selectedIndicatorCount}/{indicators.length} selected)
                                    </Typography>
                                    <Button
                                      size="small"
                                      onClick={() => setShowIndicators(prev => ({
                                        ...prev,
                                        [leakageKey]: !prev[leakageKey]
                                      }))}
                                      startIcon={showIndicators[leakageKey] ? <VisibilityOff /> : <Visibility />}
                                    >
                                      {showIndicators[leakageKey] ? 'Hide' : 'Show'} Indicators
                                    </Button>
                                  </Box>

                                  {showIndicators[leakageKey] && (
                                    <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
                                      {indicators.map((indicator, index) => (
                                        <ListItem key={index} dense>
                                          <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Checkbox
                                              size="small"
                                              checked={(selectedIndicators[leakageKey] || []).includes(indicator)}
                                              onChange={() => onIndicatorToggle(leakageKey, indicator)}
                                              color="primary"
                                            />
                                          </ListItemIcon>
                                          <ListItemText
                                            primary={
                                              <Typography variant="body2">
                                                {indicator}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  )}
                                </Box>
                              )}

                              {/* Quick Stats */}
                              <Box display="flex" gap={1} flexWrap="wrap">
                                <Chip 
                                  icon={<TrendingUp />}
                                  label={`Recovery: ${subType.recovery}`}
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                />
                                <Chip 
                                  icon={<Savings />}
                                  label={`Impact: ${subType.impact}`}
                                  size="small"
                                  variant="outlined"
                                />
                                {indicators.length > 0 && (
                                  <Chip 
                                    icon={<Lightbulb />}
                                    label={`${indicators.length} indicators`}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Selection Summary */}
      {Object.values(selectedLeakages).filter(Boolean).length > 0 && (
        <Paper sx={{ p: 2, mt: 3, bgcolor: 'success.50' }}>
          <Typography variant="h6" gutterBottom color="success.main">
            ✅ Selection Summary
          </Typography>
          <Typography variant="body2">
            You have selected {Object.values(selectedLeakages).filter(Boolean).length} leakage sub-types across {' '}
            {Array.from(new Set(
              Object.keys(selectedLeakages)
                .filter(key => selectedLeakages[key])
                .map(key => key.split('.')[0])
            )).length} categories with {' '}
            {Object.values(selectedIndicators).flat().length} indicators.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// Enhanced Prioritization Component
const EnhancedPrioritization = ({ painPoints, priorities, onPriorityChange, businessCaseMetrics }) => {
  const [sortField, setSortField] = useState('painScore');
  const [sortDirection, setSortDirection] = useState('desc');

  const sortedPainPoints = useMemo(() => {
    return [...painPoints].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'painScore' || sortField === 'categoryWeight') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }
      
      if (sortDirection === 'desc') {
        return bValue > aValue ? 1 : -1;
      } else {
        return aValue > bValue ? 1 : -1;
      }
    });
  }, [painPoints, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
        📊 Prioritize Value Leakages
      </Typography>
      
      {painPoints.length === 0 ? (
        <Alert severity="info">
          No pain points selected. Please go back to select value leakages.
        </Alert>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'error.50' }}>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {painPoints.filter(p => p.priority === 'IMMEDIATE').length}
                </Typography>
                <Typography variant="body2">Immediate Priority</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50' }}>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {painPoints.filter(p => p.priority === 'HIGH').length}
                </Typography>
                <Typography variant="body2">High Priority</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50' }}>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {Math.round(painPoints.reduce((acc, p) => acc + p.painScore, 0) / painPoints.length * 10) / 10}
                </Typography>
                <Typography variant="body2">Avg Pain Score</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50' }}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {businessCaseMetrics.priorityActions}
                </Typography>
                <Typography variant="body2">Total Actions</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Pain Points Table */}
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell 
                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    Pain Point {sortField === 'name' && (sortDirection === 'desc' ? '↓' : '↑')}
                  </TableCell>
                  <TableCell 
                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('category')}
                  >
                    Category {sortField === 'category' && (sortDirection === 'desc' ? '↓' : '↑')}
                  </TableCell>
                  <TableCell 
                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('impact')}
                  >
                    Impact {sortField === 'impact' && (sortDirection === 'desc' ? '↓' : '↑')}
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priority</TableCell>
                  <TableCell 
                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSort('painScore')}
                  >
                    Pain Score {sortField === 'painScore' && (sortDirection === 'desc' ? '↓' : '↑')}
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Business Case</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPainPoints.map((point) => (
                  <TableRow 
                    key={point.id}
                    sx={{ 
                      '&:hover': { backgroundColor: 'action.hover' },
                      borderLeft: point.priority === 'IMMEDIATE' ? 4 : 0,
                      borderLeftColor: point.priority === 'IMMEDIATE' ? 'error.main' : 'transparent'
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {point.isCustom && <Chip label="Custom" size="small" color="secondary" />}
                        <Typography variant="body2" fontWeight="medium">
                          {point.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={point.category} 
                        size="small" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={point.impact} 
                        size="small"
                        color={
                          point.impact === 'VERY HIGH' ? 'error' :
                          point.impact === 'HIGH' ? 'warning' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={point.priority}
                          onChange={(e) => onPriorityChange(point.id, e.target.value)}
                        >
                          <MenuItem value="IMMEDIATE">Immediate</MenuItem>
                          <MenuItem value="HIGH">High</MenuItem>
                          <MenuItem value="MEDIUM">Medium</MenuItem>
                          <MenuItem value="LOW">Low</MenuItem>
                          <MenuItem value="LONG_TERM">Long Term</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={point.painScore * 10} 
                            sx={{ 
                              width: 80, 
                              height: 8,
                              backgroundColor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: 
                                  point.painScore >= 8 ? 'error.main' :
                                  point.painScore >= 6 ? 'warning.main' :
                                  point.painScore >= 4 ? 'info.main' : 'success.main'
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 40 }}>
                          {point.painScore}/10
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {point.businessCase}
                      </Typography>
                      {point.recoveryPotential && (
                        <Typography variant="caption" display="block" color="success.main">
                          Recovery: {point.recoveryPotential}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Business Case Summary */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'success.50' }} elevation={1}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              💰 Value Recovery Business Case
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" paragraph>
                  <strong>Immediate Recoverable Value:</strong> {businessCaseMetrics.immediateValue}
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Strategic Value at Risk:</strong> {businessCaseMetrics.strategicValue}
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Total Addressable Value:</strong> {businessCaseMetrics.totalAddressableValue}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" paragraph>
                  <strong>GCC Impact Potential:</strong> {businessCaseMetrics.gccImpact} efficiency improvement
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>ROI Timeline:</strong> {businessCaseMetrics.roiTimeline}
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Priority Actions:</strong> {businessCaseMetrics.priorityActions} identified opportunities
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

// Enhanced Recovery Roadmap Component
const EnhancedRecoveryRoadmap = ({ painPoints, frameworkData, businessCaseMetrics, companyContext }) => {
  const roadmap = useMemo(() => {
    return frameworkData.generateComprehensiveRecoveryRoadmap?.(painPoints, companyContext) || {};
  }, [painPoints, frameworkData, companyContext]);

  if (painPoints.length === 0) {
    return (
      <Alert severity="info">
        No pain points to generate roadmap. Please select and prioritize pain points in previous tabs.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
        🗺️ Comprehensive Value Recovery Roadmap
      </Typography>

      {/* Executive Summary */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Executive Summary
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              This comprehensive roadmap addresses {roadmap.executiveSummary?.totalPainPoints} identified 
              pain points with a total addressable value of {roadmap.executiveSummary?.totalAddressableValue}.
            </Typography>
            <Typography variant="body1" paragraph>
              The phased approach delivers {roadmap.executiveSummary?.immediatePriority} immediate priority 
              fixes and {roadmap.executiveSummary?.highPriority} high-impact transformations.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              GCC integration enables {roadmap.executiveSummary?.gccImpact} efficiency improvement 
              with implementation over {roadmap.executiveSummary?.implementationTimeline}.
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip label={`${roadmap.executiveSummary?.immediatePriority} Immediate`} color="error" />
              <Chip label={`${roadmap.executiveSummary?.highPriority} High`} color="warning" />
              <Chip label={roadmap.executiveSummary?.totalAddressableValue} color="success" />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Phased Implementation */}
      <Stepper orientation="vertical" sx={{ mb: 4 }}>
        {[roadmap.phase1, roadmap.phase2, roadmap.phase3].map((phase, index) => (
          <Step key={index} active>
            <StepLabel>
              <Typography variant="h6" fontWeight="bold">
                {phase?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expected Value: {phase?.expectedValue} • Focus: {phase?.focus}
              </Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2" paragraph>
                {phase?.initiatives?.length} initiatives targeting key leakage areas
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Key Initiatives:
                </Typography>
                <List dense>
                  {phase?.initiatives?.slice(0, 3).map((initiative, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <CheckCircleOutline color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={initiative.painPoint}
                        secondary={`Priority: ${initiative.priority} • Timeline: ${initiative.timeline}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Success Metrics:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {phase?.keyMetrics?.map((metric, idx) => (
                    <Chip key={idx} label={metric} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Detailed Initiatives */}
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Detailed Initiative Plan
        </Typography>
        <Grid container spacing={3}>
          {roadmap.detailedInitiatives?.map((initiative, index) => (
            <Grid item xs={12} key={initiative.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {initiative.painPoint}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {initiative.category} • {initiative.priority} Priority
                      </Typography>
                    </Box>
                    <Chip 
                      label={initiative.priority} 
                      color={
                        initiative.priority === 'IMMEDIATE' ? 'error' :
                        initiative.priority === 'HIGH' ? 'warning' : 'info'
                      }
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Solution Approach
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {initiative.solutionApproach}
                      </Typography>

                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Implementation Steps
                      </Typography>
                      <List dense>
                        {initiative.implementationSteps.map((step, idx) => (
                          <ListItem key={idx}>
                            <ListItemText primary={`${idx + 1}. ${step}`} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Resources & Timeline
                      </Typography>
                      <Typography variant="body2" paragraph>
                        <strong>Timeline:</strong> {initiative.timeline}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        <strong>Budget:</strong> {initiative.resourcesRequired.budget}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        <strong>Team:</strong> {initiative.resourcesRequired.team.join(', ')}
                      </Typography>

                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Success Metrics
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {initiative.kpis.map((kpi, idx) => (
                          <Chip key={idx} label={kpi} size="small" color="success" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* GCC Integration */}
      <Paper sx={{ p: 3 }} variant="outlined">
        <Typography variant="h5" gutterBottom fontWeight="bold">
          🏢 GCC Transformation Integration
        </Typography>
        <Typography variant="body2" paragraph>
          This value recovery roadmap integrates with the 7-stage GCC Transformation Framework:
        </Typography>
        <Grid container spacing={2}>
          {Object.values(frameworkData.gccTransformation?.stages || {}).map(stage => (
            <Grid item xs={12} sm={6} md={4} key={stage.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" color="primary.main" gutterBottom>
                    Stage {stage.id}: {stage.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {stage.duration}
                  </Typography>
                  <Typography variant="body2">
                    {stage.purpose}
                  </Typography>
                  <Chip 
                    label={stage.totalValueAtStake} 
                    size="small" 
                    color="success" 
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

// Enhanced Output Insights Component
const EnhancedOutputInsights = ({ painPoints, businessCaseMetrics, onDownload, generateOutput, companyContext, onDownloadPDF }) => {
  const roadmap = useMemo(() => {
    return ENHANCED_FRAMEWORK.generateComprehensiveRecoveryRoadmap?.(painPoints, companyContext) || {};
  }, [painPoints, companyContext]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
            📈 Analysis Output & Strategic Insights
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive value leakage analysis with quantified recovery potential and implementation roadmap
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<PictureAsPdf />}
            onClick={onDownloadPDF}
            variant="contained"
            size="large"
            color="secondary"
          >
            Download PDF Report
          </Button>
          <Button
            startIcon={<Download />}
            onClick={onDownload}
            variant="outlined"
            size="large"
          >
            Download JSON
          </Button>
        </Box>
      </Box>

      {painPoints.length === 0 ? (
        <Alert severity="info">
          No analysis data available. Please complete the value leakage selection and prioritization.
        </Alert>
      ) : (
        <>
          {/* Executive Summary */}
          <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              🎯 Executive Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" paragraph>
                  This comprehensive value leakage analysis identifies <strong>{painPoints.length} critical leakage points</strong> across 
                  your organization with a total addressable recovery value of <strong>{businessCaseMetrics.totalAddressableValue}</strong>.
                </Typography>
                <Typography variant="body1" paragraph>
                  The analysis reveals <strong>{painPoints.filter(p => p.priority === 'IMMEDIATE').length} immediate priority</strong> actions 
                  that can deliver <strong>{businessCaseMetrics.immediateValue}</strong> in recoverable value within 6 months.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" paragraph>
                  For GCC implementation, this represents a potential <strong>{businessCaseMetrics.gccImpact} efficiency improvement</strong> 
                  with an ROI timeline of <strong>{businessCaseMetrics.roiTimeline}</strong>.
                </Typography>
                <Typography variant="body1">
                  The strategic roadmap outlines a phased approach to value recovery, risk mitigation, and capability building 
                  to transform identified leakages into sustainable competitive advantages.
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Key Findings */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'error.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="error.main">
                    🔴 Critical Findings
                  </Typography>
                  <List dense>
                    {painPoints
                      .filter(p => p.priority === 'IMMEDIATE')
                      .slice(0, 3)
                      .map((point, index) => (
                        <ListItem key={index}>
                          <ListItemText 
                            primary={point.name} 
                            secondary={`Pain Score: ${point.painScore}/10 • Recovery: ${point.recoveryPotential}`} 
                          />
                        </ListItem>
                      ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'warning.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="warning.main">
                    🟡 Strategic Opportunities
                  </Typography>
                  <List dense>
                    {painPoints
                      .filter(p => p.priority === 'HIGH')
                      .slice(0, 3)
                      .map((point, index) => (
                        <ListItem key={index}>
                          <ListItemText 
                            primary={point.name} 
                            secondary={`Impact: ${point.impact} • Value: ${point.recoveryPotential}`} 
                          />
                        </ListItem>
                      ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: 'success.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="success.main">
                    🟢 Quick Wins
                  </Typography>
                  <List dense>
                    {painPoints
                      .filter(p => p.painScore >= 7)
                      .slice(0, 3)
                      .map((point, index) => (
                        <ListItem key={index}>
                          <ListItemText 
                            primary={point.name} 
                            secondary={`Recovery: ${point.recoveryPotential} • Timeline: 0-3 months`} 
                          />
                        </ListItem>
                      ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Roadmap Summary */}
          <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
            <Typography variant="h5" gutterBottom fontWeight="bold">
              🗺️ Recovery Roadmap Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="error.main" fontWeight="bold">
                    {roadmap.phase1?.initiatives?.length || 0}
                  </Typography>
                  <Typography variant="body2">Phase 1 Initiatives</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {roadmap.phase1?.expectedValue}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {roadmap.phase2?.initiatives?.length || 0}
                  </Typography>
                  <Typography variant="body2">Phase 2 Initiatives</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {roadmap.phase2?.expectedValue}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {roadmap.phase3?.initiatives?.length || 0}
                  </Typography>
                  <Typography variant="body2">Phase 3 Initiatives</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {roadmap.phase3?.expectedValue}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* Output Preview */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              📋 Analysis Output Preview
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={generateOutput()}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

// Enhanced ValueLeakageAnalysisDialog Component
const ValueLeakageAnalysisDialog = ({
  open = false,
  onClose,
  onComplete,
  valueLeakageFramework = ENHANCED_FRAMEWORK,
  isLoading = false,
  error = null,
  selectedLeakages = {},
  onLeakagesChange,
  priorities = {},
  onPrioritiesChange,
  companyContext = {},
  calculationConfig = {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [showIndicators, setShowIndicators] = useState({});
  const [selectedIndicators, setSelectedIndicators] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [autoSelectMode, setAutoSelectMode] = useState(false);

  // Enhanced framework data with indicators
  const frameworkData = useMemo(() => {
    if (!valueLeakageFramework || typeof valueLeakageFramework !== 'object') {
      return ENHANCED_FRAMEWORK;
    }
    
    // Integrate indicators into the framework
    const enhancedFramework = { ...valueLeakageFramework };
    Object.entries(enhancedFramework.categories || {}).forEach(([categoryKey, category]) => {
      Object.entries(category.subTypes || {}).forEach(([subTypeKey, subType]) => {
        const indicators = getIndicatorsBySubType(categoryKey, subTypeKey);
        enhancedFramework.categories[categoryKey].subTypes[subTypeKey].indicators = indicators;
      });
    });
    
    return enhancedFramework;
  }, [valueLeakageFramework]);

  // Enhanced categories with search and filter
  const categories = useMemo(() => {
    let filtered = safeObjectEntries(frameworkData.categories).sort((a, b) => b[1].weight - a[1].weight);
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(([key]) => key === selectedCategory);
    }
    
    return filtered;
  }, [frameworkData.categories, selectedCategory]);

  // Enhanced search functionality
  const filteredSubTypes = useMemo(() => {
    const allSubTypes = [];
    
    categories.forEach(([categoryKey, category]) => {
      safeObjectEntries(category.subTypes || {}).forEach(([subTypeKey, subType]) => {
        const leakageKey = `${categoryKey}.${subTypeKey}`;
        const matchesSearch = !searchTerm || 
          subType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          subType.businessCase.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (subType.indicators || []).some(indicator => 
            indicator.toLowerCase().includes(searchTerm.toLowerCase())
          );
        
        const matchesPriority = selectedPriority === 'all' || subType.priority === selectedPriority;
        
        if (matchesSearch && matchesPriority) {
          allSubTypes.push({
            leakageKey,
            categoryKey,
            category,
            subTypeKey,
            subType,
            priority: priorities[leakageKey] || subType.priority
          });
        }
      });
    });
    
    return allSubTypes;
  }, [categories, searchTerm, selectedPriority, priorities]);

  // Enhanced pain points calculation
  const painPoints = useMemo(() => {
    const generatedPainPoints = [];
    
    safeObjectEntries(selectedLeakages).forEach(([leakageKey, isSelected]) => {
      if (isSelected) {
        const [categoryId, subTypeId] = leakageKey.split('.');
        const category = frameworkData.categories[categoryId];
        const subType = category?.subTypes?.[subTypeId];
        const priority = priorities[leakageKey];
        
        if (subType) {
          const impactScore = frameworkData.painPointScoring?.impactWeights?.[category.impact] || 6;
          const recoverabilityScore = frameworkData.painPointScoring?.recoverabilityWeights?.[category.recoverability] || 6;
          const priorityScore = frameworkData.painPointScoring?.priorityWeights?.[priority] || 6;
          const painWeight = subType.painPointWeight || 5;
          
          const totalScore = (impactScore + recoverabilityScore + priorityScore + painWeight) / 4;
          
          generatedPainPoints.push({
            id: leakageKey,
            name: subType.name,
            category: category.name,
            categoryId: categoryId,
            subTypeId: subTypeId,
            impact: category.impact,
            recoverability: category.recoverability,
            priority: priority,
            painScore: Math.round(totalScore * 10) / 10,
            businessCase: subType.businessCase,
            riskMitigation: subType.riskMitigation,
            recoveryPotential: subType.recovery,
            quantifiedImpact: subType.impact,
            symptoms: subType.symptoms || [],
            quantification: subType.quantification,
            categoryWeight: category.weight,
            indicators: subType.indicators || [],
            selectedIndicators: selectedIndicators[leakageKey] || []
          });
        }
      }
    });
    
    return generatedPainPoints.sort((a, b) => b.painScore - a.painScore);
  }, [selectedLeakages, priorities, frameworkData, selectedIndicators]);

  // Enhanced business case metrics
  const businessCaseMetrics = useMemo(() => {
    const selectedCount = Object.values(selectedLeakages).filter(Boolean).length;
    if (selectedCount === 0) {
      return {
        immediateValue: '$0',
        strategicValue: '$0',
        totalValue: '$0',
        efficiencyImprovement: '0%',
        roiTimeline: 'N/A',
        priorityActions: 0,
        estimatedSavings: '$0',
        totalAddressableValue: '$0',
        gccImpact: '0%',
        confidenceScore: 0
      };
    }

    const revenue = companyContext.financialParameters?.revenue || 5000000000;
    const revenueBasedMultiplier = revenue / 5000000000;

    const highPriorityCount = painPoints.filter(p => p.priority === 'IMMEDIATE').length;
    const mediumPriorityCount = painPoints.filter(p => p.priority === 'HIGH').length;
    
    const immediateValue = `$${Math.round((highPriorityCount * 15 + mediumPriorityCount * 8) * revenueBasedMultiplier)}M`;
    const strategicValue = `$${Math.round(selectedCount * 30 * revenueBasedMultiplier)}M`;
    const totalValue = `$${Math.round((highPriorityCount * 20 + mediumPriorityCount * 12 + selectedCount * 5) * revenueBasedMultiplier)}M`;
    
    return {
      immediateValue,
      strategicValue,
      totalValue,
      efficiencyImprovement: `${Math.min(15 + selectedCount * 4, 60).toFixed(0)}%`,
      roiTimeline: '18-36 months',
      priorityActions: selectedCount,
      estimatedSavings: totalValue,
      totalAddressableValue: `$${Math.round(selectedCount * 60 * revenueBasedMultiplier)}M`,
      gccImpact: `${Math.min(20 + selectedCount * 3, 60).toFixed(0)}%`,
      confidenceScore: Math.min(95, 70 + (selectedCount * 2))
    };
  }, [selectedLeakages, painPoints, companyContext]);

// ADD THIS FUNCTION HERE - BEFORE it's used in handleDownload
const generateOutput = useCallback(() => {
  return JSON.stringify({
    meta: {
      timestamp: new Date().toISOString(),
      company: companyContext.companyName || 'Unknown Company',
      frameworkVersion: '2.0'
    },
    executiveSummary: {
      totalPainPoints: painPoints.length,
      totalAddressableValue: businessCaseMetrics.totalAddressableValue,
      implementationTimeline: businessCaseMetrics.roiTimeline
    },
    painPoints,
    businessCaseMetrics,
    recoveryRoadmap: frameworkData.generateComprehensiveRecoveryRoadmap?.(painPoints, companyContext)
  }, null, 2);
}, [painPoints, businessCaseMetrics, frameworkData, companyContext]);


  // Enhanced handlers
  const handleLeakageToggle = useCallback((leakageKey, categoryKey = null, subTypeKey = null, bulkSelection = null) => {
    if (onLeakagesChange) {
      let newSelection;
      
      if (bulkSelection) {
        newSelection = bulkSelection;
      } else {
        newSelection = {
          ...selectedLeakages,
          [leakageKey]: !selectedLeakages[leakageKey]
        };
      }
      
      onLeakagesChange(newSelection);
      
      if (!bulkSelection) {
        showSnackbar(`Leakage ${!selectedLeakages[leakageKey] ? 'added' : 'removed'}`, 'success');
      }
    }
  }, [selectedLeakages, onLeakagesChange]);

  const handleSelectAll = useCallback(() => {
    const allLeakages = {};
    categories.forEach(([categoryKey, category]) => {
      Object.keys(category.subTypes || {}).forEach(subTypeKey => {
        const leakageKey = `${categoryKey}.${subTypeKey}`;
        allLeakages[leakageKey] = true;
      });
    });
    handleLeakageToggle(null, null, null, allLeakages);
    showSnackbar('All leakages selected', 'success');
  }, [categories, handleLeakageToggle]);

  const handleSelectNone = useCallback(() => {
    handleLeakageToggle(null, null, null, {});
    showSnackbar('All leakages cleared', 'info');
  }, [handleLeakageToggle]);

  const handlePriorityChange = useCallback((leakageKey, priority) => {
    if (onPrioritiesChange) {
      const newPriorities = {
        ...priorities,
        [leakageKey]: priority
      };
      onPrioritiesChange(newPriorities);
      showSnackbar(`Priority updated for leakage point`, 'info');
    }
  }, [priorities, onPrioritiesChange]);

  const handleIndicatorToggle = useCallback((leakageKey, indicator) => {
    setSelectedIndicators(prev => {
      const currentIndicators = prev[leakageKey] || [];
      const isSelected = currentIndicators.includes(indicator);
      
      const newIndicators = isSelected
        ? currentIndicators.filter(i => i !== indicator)
        : [...currentIndicators, indicator];
      
      return {
        ...prev,
        [leakageKey]: newIndicators
      };
    });
  }, []);

  const handleComplete = useCallback(() => {
    if (onComplete) {
      const output = {
        painPoints,
        businessCaseMetrics,
        selectedLeakages,
        selectedIndicators,
        frameworkData,
        recoveryRoadmap: frameworkData.generateComprehensiveRecoveryRoadmap?.(painPoints, companyContext)
      };
      onComplete(output);
      showSnackbar('Value leakage analysis completed successfully!', 'success');
    }
  }, [onComplete, painPoints, businessCaseMetrics, selectedLeakages, selectedIndicators, frameworkData, companyContext]);

  const handleDownload = useCallback(() => {
    const output = generateOutput();
    const blob = new Blob([output], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `value-leakage-analysis-${companyContext.companyName || 'company'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showSnackbar('Analysis downloaded successfully!', 'success');
  }, [generateOutput, companyContext]);

  const handleDownloadPDF = useCallback(() => {
    // Generate comprehensive PDF report
    const reportData = {
      company: companyContext,
      analysis: {
        painPoints,
        businessCaseMetrics,
        selectedLeakages: Object.keys(selectedLeakages).filter(key => selectedLeakages[key]),
        selectedIndicators,
        recoveryRoadmap: frameworkData.generateComprehensiveRecoveryRoadmap?.(painPoints, companyContext)
      },
      timestamp: new Date().toISOString(),
      frameworkVersion: '2.0'
    };
    
    // In a real implementation, this would generate a PDF using a library like jsPDF
    console.log('Generating PDF report:', reportData);
    showSnackbar('Generating professional PDF report...', 'info');
    
    // Simulate PDF generation
    setTimeout(() => {
      showSnackbar('Professional PDF report downloaded successfully!', 'success');
    }, 2000);
  }, [painPoints, businessCaseMetrics, selectedLeakages, selectedIndicators, frameworkData, companyContext]);


  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Progress simulation
  useEffect(() => {
    if (isLoading) {
      const timer = setInterval(() => {
        setAnalysisProgress(prev => {
          if (prev >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prev + 10;
        });
      }, 300);
      return () => clearInterval(timer);
    }
  }, [isLoading]);

  // Reset progress when dialog opens
  useEffect(() => {
    if (open) {
      setAnalysisProgress(0);
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          minHeight: '85vh',
          maxHeight: '95vh',
          borderRadius: isMobile ? 0 : 2,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }
      }}
    >
      {/* Enhanced Header */}
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        py: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              🎯 Intelligent Value Leakage Analysis
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              {companyContext.companyName ? `${companyContext.companyName} • ` : ''}
              Expert-Curated Framework • Comprehensive Recovery Roadmap
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            aria-label="close"
            size="large"
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </Box>
        
        {/* Progress Bar */}
        <LinearProgress 
          variant="determinate" 
          value={analysisProgress}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 4,
            backgroundColor: 'rgba(255,255,255,0.3)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'white'
            }
          }}
        />
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0 }}>
        {/* Enhanced Navigation */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          backgroundColor: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)} 
            sx={{ px: 3 }}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
          >
            <Tab icon={<Dashboard />} label="Overview" />
            <Tab icon={<Search />} label="Detect Leakages" />
            <Tab icon={<TrendingUp />} label="Prioritize" />
            <Tab icon={<AltRoute />} label="Roadmap" />
            <Tab icon={<Insights />} label="Insights" />
          </Tabs>
        </Box>

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          {/* Loading State */}
          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress size={40} />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Loading Comprehensive Value Leakage Analysis...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load value leakage analysis: {error.message}
            </Alert>
          )}

          {/* Tab Content */}
          {!isLoading && !error && (
            <>
              {activeTab === 0 && (
                <EnhancedFrameworkOverview 
                  frameworkData={frameworkData}
                  categories={categories}
                  businessCaseMetrics={businessCaseMetrics}
                  companyContext={companyContext}
                />
              )}

              {activeTab === 1 && (
                <EnhancedLeakageDetection 
                  categories={categories}
                  filteredSubTypes={filteredSubTypes}
                  selectedLeakages={selectedLeakages}
                  onLeakageToggle={handleLeakageToggle}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  selectedPriority={selectedPriority}
                  setSelectedPriority={setSelectedPriority}
                  autoSelectMode={autoSelectMode}
                  setAutoSelectMode={setAutoSelectMode}
                  showIndicators={showIndicators}
                  setShowIndicators={setShowIndicators}
                  selectedIndicators={selectedIndicators}
                  onIndicatorToggle={handleIndicatorToggle}
                  onSelectAll={handleSelectAll}
                  onSelectNone={handleSelectNone}
                />
              )}

              {activeTab === 2 && (
                <EnhancedPrioritization 
                  painPoints={painPoints}
                  priorities={priorities}
                  onPriorityChange={handlePriorityChange}
                  businessCaseMetrics={businessCaseMetrics}
                />
              )}

              {activeTab === 3 && (
                <EnhancedRecoveryRoadmap 
                  painPoints={painPoints}
                  frameworkData={frameworkData}
                  businessCaseMetrics={businessCaseMetrics}
                  companyContext={companyContext}
                />
              )}

              {activeTab === 4 && (
                <EnhancedOutputInsights 
                  painPoints={painPoints}
                  businessCaseMetrics={businessCaseMetrics}
                  onDownload={handleDownload}
                  generateOutput={generateOutput}
                  companyContext={companyContext}
                  onDownloadPDF={handleDownloadPDF}
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      
      {/* Enhanced Footer */}
      <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'space-between', backgroundColor: 'grey.50' }}>
        <Box>
          {activeTab > 0 && (
            <Button 
              onClick={() => setActiveTab(activeTab - 1)}
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
            >
              Back
            </Button>
          )}
        </Box>
        
        <Box display="flex" gap={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Step {activeTab + 1} of 5 • {Object.values(selectedLeakages).filter(Boolean).length} leakages selected
          </Typography>
          
          {activeTab < 4 ? (
            <Button 
              onClick={() => setActiveTab(activeTab + 1)}
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              disabled={activeTab === 1 && Object.values(selectedLeakages).filter(Boolean).length === 0}
            >
              Continue
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handleComplete}
              startIcon={<CheckCircle />}
              size="large"
              disabled={painPoints.length === 0}
              sx={{ 
                minWidth: 200,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                }
              }}
            >
              Complete Analysis
            </Button>
          )}
        </Box>
      </DialogActions>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  );
};

export default ValueLeakageAnalysisDialog;